/**
 * `useRoiCards` — the ROI card definitions with their real values injected.
 *
 * Lifted VERBATIM out of `AnalyticsTabs` when the role tabs became seeded views. It is
 * here rather than deleted because it is the only place that knows how to turn the
 * `/analytics/admin/roi` response into card values, and losing it would have quietly
 * reverted the three Marketing cards to placeholders.
 *
 * It returns definitions, not JSX, so nothing here depends on how a card is drawn —
 * the caller maps a `ROICardDef` into an `AnalyticsCardItem` for the canvas.
 *
 * ## It takes the response; it does NOT fetch
 *
 * It used to issue its own `useAnalyticsRoi` call, and that call omitted `company_ids`
 * while `AnalyticsPage`'s hero call included it. Two consequences, and only the first
 * was obvious:
 *
 *  1. Different query keys, so the page fired TWO `/analytics/admin/roi` requests per
 *     filter change for one endpoint's worth of data.
 *  2. Worse, and the reason this is a correctness fix rather than a tidy-up: the
 *     response lands in ONE redux slot (`state.analytics.roi`), which
 *     `SET_ANALYTICS_ROI` overwrites wholesale. Two in-flight requests with different
 *     scoping raced into it, last-to-resolve winning — so the hero and these cards
 *     both read whichever arrived second, non-deterministically. The unscoped request
 *     winning meant the hero's denominator, and these cards, could be measured across
 *     EVERY company. The same hazard the totals comparison was designed around.
 *
 * So the page hoists one company-scoped request and feeds both consumers from it.
 * These cards are now scoped to the same single company as the hero, which is also
 * what makes the cross-tenant hazard enumerated in `ROICards`' header moot.
 */
import { useMemo } from 'react'

import { METRIC_BY_ID, type IRoiMetrics, type IRoiSummary } from '@repo/types'

import { RoiFigureWithAssumption, formatCents, missingInputsFor } from './metricInputs'
import type { ROICardDef } from './ROICards'
import { ALL_ROI_CARDS, type ROITabKey } from './roiCardDefinitions'

/**
 * Fill in a registry-backed card from the ROI SUMMARY (VRG-459).
 *
 * Four states, and the difference between the last three is the whole point — see
 * `MetricInputsPopover` for why conflating them misleads in opposite directions:
 *
 *   contributing              a figure, from `contributions[]`.
 *   withheld `needs_input`    `N inputs needed` — the brand can fix this.
 *   withheld `not_computed`   em-dash + why. Every input is set; Vera still cannot
 *                             produce the measured half. Nothing the brand does helps.
 *   blocked                   em-dash + the registry's own `blocked.reason`. Not in
 *                             `withheld[]` at all: a blocked metric is not a candidate,
 *                             so counting it would make the hero read "2 of 6" against
 *                             a target of 6 that can never be reached.
 */
const applySummary = (
  card: ROICardDef,
  summary?: IRoiSummary,
  onSaved?: () => void,
  // VERA-260 — the company's reporting currency. Threaded in rather than read from a
  // module constant because it decides WHICH ROWS these figures contain, not just how
  // they are drawn: money aggregates include only rows already priced in it. It lives
  // on `IRoiMetrics` rather than on the summary, hence the extra argument.
  currency?: string,
): ROICardDef => {
  if (!card.metricId) return card

  const metric = METRIC_BY_ID[card.metricId]
  if (metric?.blocked) {
    return { ...card, value: null, blockedReason: metric.blocked.reason }
  }

  if (!summary) return card

  const contribution = summary.contributions.find(c => c.metric_id === card.metricId)
  if (contribution) {
    const figure = formatCents(contribution.cents, currency)

    /*
      ⚠️ AN ASSUMPTION IS NOW A MARKER BESIDE THE FIGURE, NOT AN AMBER SENTENCE UNDER IT.

      This REVERSES the epic's locked "stated inline, never in a tooltip" rule, and the
      reversal is the user's call rather than a drift. The inline rule was right about
      one reader and wrong about the page: the caveat rendered on EVERY assumption-backed
      card, so a screen of amber sentences sat beside amber `N inputs needed` pills and
      the warning tone stopped meaning anything. A caveat that is always on is furniture.

      What the inline rule protected is preserved by construction, and it is the reason
      the marker is a tinted disc at the figure's side rather than a grey glyph in a
      corner: the disclosure stays VISIBLE WITHOUT HOVER and ADJACENT TO THE FIGURE, so
      a reader looking at the number still sees that it rests on something they supplied.
      Only the sentence moved into the tooltip — and the marker gained the thing the
      sentence never had, which is the click that opens the figure. See
      `MetricAssumptionMarker`.

      `card.note` is deliberately NOT set here any more. It stays the slot for a caveat
      on the ARITHMETIC (Geographic Reach's unattributed scans), which is a different
      claim from "this rests on your own number" and is not resolvable by opening a form.
    */
    return {
      ...card,
      value: contribution.assumption_based ? (
        <RoiFigureWithAssumption
          figure={figure}
          metricId={card.metricId}
          metricTitle={card.title}
          roi={summary}
          onSaved={onSaved}
        />
      ) : (
        figure
      ),
      /*
        The export's copy of the same number — see `ROICardDef.rawValue`.

        This is the branch that most needs it: `value` here is either a GROUPED currency
        string or a React ELEMENT, and neither can be turned back into a number. Major
        units, from the same `contribution.cents` the figure beside it is formatted from,
        so the two can never disagree.
      */
      rawValue: String(contribution.cents / 100),
    }
  }

  const missing = missingInputsFor(summary, card.metricId)
  if (missing && missing.length > 0) {
    return { ...card, value: null, needsInputs: missing }
  }

  const withheld = summary.withheld.find(w => w.metric_id === card.metricId)
  if (withheld) {
    return {
      ...card,
      value: null,
      // Deliberately says what is missing WITHOUT implying the brand can supply it.
      // "Add your figures" here would be the false promise this state exists to avoid.
      //
      // ⚠️ It used to open "Vera HAS your figures for this", and that clause became
      // false. It was true only while `resolveRoiContributions` tested missing inputs
      // BEFORE looking for a resolver, which meant nothing could reach `not_computed`
      // until every input was set. With the ordering fixed, a resolver-less metric
      // reports `not_computed` whatever its inputs — so this card now renders with the
      // figures unsupplied, and telling that reader we already have their numbers is
      // both wrong and confusing in the one direction that matters: it implies there is
      // nothing left for them to do, on a page whose whole job is to say what is.
      //
      // What survives is the half that does not depend on input state at all.
      blockedReason:
        'Vera does not yet measure the other half of this calculation, so no figure you supply would change it.',
    }
  }

  return card
}

export const useRoiCards = (
  roi?: IRoiMetrics,
  isLoading = false,
  /**
   * The hero's summary from the SAME response — `contributions`, `withheld` and the
   * spend. Optional so a caller with only the per-metric figures still works; the
   * registry-backed cards then stay at their pre-fetch em-dash rather than guessing.
   */
  summary?: IRoiSummary,
  /**
   * Refetch the ROI after a figure is saved from an assumption marker's popover, so
   * the card's number moves in place.
   *
   * ⚠️ OPTIONAL, AND CURRENTLY UNPASSED BY THE ANALYTICS PAGE. Without it a save from
   * the marker persists and the card keeps showing the OLD figure until a reload —
   * `MetricInputsNeededButton` gets `onSaved={() => refetchRoi()}` at its own call
   * site and this one wants the same. Left as a parameter rather than faked from
   * here: the ROI lives in a redux slot keyed on the page's filters (see this file's
   * header), and re-dispatching it without those filters is the cross-tenant scoping
   * hazard that header is about. One line at the call site closes it.
   */
  onSaved?: () => void,
) => {
  const marketing = useMemo<ROICardDef[]>(() => {
    return ALL_ROI_CARDS.marketing.map(card => {
      switch (card.id) {
        case 'mkt_invalid_scans':
          // The unattributed count is this card's equivalent of Geographic Reach's
          // note, and it exists for the same reason: the figure is arithmetically
          // right and silently incomplete. A scan of a tag Vera never issued is the
          // most interesting invalid scan there is, and it cannot be in the count
          // above because every filter on this endpoint reaches through a join to
          // `certificates` that a certificate-less scan does not survive. Saying so
          // is the difference between an honest number and a reassuring one.
          return {
            ...card,
            value: roi ? String(roi.invalid_scans) : null,
            note:
              roi && roi.invalid_scans_unattributed > 0
                ? `${roi.invalid_scans_unattributed} further invalid ${
                    roi.invalid_scans_unattributed === 1 ? 'scan' : 'scans'
                  } matched no certificate at all, so ${
                    roi.invalid_scans_unattributed === 1 ? 'it is' : 'they are'
                  } not counted here.`
                : undefined,
          }
        case 'mkt_invalid_scan_rate':
          return {
            ...card,
            // `typeof === 'number'`, for the reason spelled out on repeat_scan_rate
            // below: a null check alone lets UNDEFINED through into `.toFixed`, which
            // throws inside a `useMemo` during render and bounces the user off the
            // whole page via the global ErrorBoundary.
            value:
              typeof roi?.invalid_scan_rate === 'number'
                ? `${roi.invalid_scan_rate.toFixed(1)}%`
                : null,
            // Without the `%`, so the export's column is a number rather than text.
            rawValue:
              typeof roi?.invalid_scan_rate === 'number'
                ? roi.invalid_scan_rate.toFixed(1)
                : null,
            // The basis, not just the rate — "100.0%" over one scan is correct and
            // meaningless, and the two are indistinguishable without the denominator.
            unit: roi
              ? `${roi.invalid_scans} of ${roi.invalid_scan_denominator} ${
                  roi.invalid_scan_denominator === 1 ? 'scan' : 'scans'
                }`
              : card.unit,
          }
        case 'mkt_leads_captured':
          return { ...card, value: roi ? String(roi.leads_captured) : null }
        case 'mkt_time_to_first_scan':
          return {
            ...card,
            value:
              typeof roi?.median_days_to_first_scan === 'number'
                ? `${roi.median_days_to_first_scan.toFixed(1)}`
                : null,
          }
        case 'mkt_geo_reach':
          // The unattributed count is the difference between what the map draws and
          // what this card can count. Saying so is the whole point: "0 countries"
          // beside a visible pin reads as a bug unless the page explains it.
          return {
            ...card,
            value: roi ? String(roi.geographic_reach) : null,
            note:
              roi && roi.geographic_reach_unattributed > 0
                ? `${roi.geographic_reach_unattributed} located ${
                    roi.geographic_reach_unattributed === 1 ? 'scan is' : 'scans are'
                  } on the map without a resolved country, so ${
                    roi.geographic_reach_unattributed === 1 ? 'it is' : 'they are'
                  } not counted here.`
                : undefined,
          }
        case 'mkt_repeat_scan_rate':
          // Show the BASIS, not just the rate. "100.0%" over a denominator of 2 is
          // correct arithmetic and a meaningless statistic, and the two are
          // indistinguishable without the base.
          return {
            ...card,
            // `typeof === 'number'`, NOT `!== null`. The declared type is
            // `number | null`, but a check against null alone passes UNDEFINED
            // straight through into `.toFixed` — and that throws during render,
            // inside a `useMemo`, which the global ErrorBoundary catches by bouncing
            // the user off the whole page. This exact crash was observed live when
            // the caller passed the response's `roi` (an `IRoiSummary`, which has no
            // such field) instead of its `data`. The call site is fixed; this makes
            // the same mistake, or an API build that omits the field, degrade to the
            // em-dash placeholder rather than take the page down.
            value:
              typeof roi?.repeat_scan_rate === 'number'
                ? `${roi.repeat_scan_rate.toFixed(1)}%`
                : null,
            // Without the `%` — see the sibling rate above.
            rawValue:
              typeof roi?.repeat_scan_rate === 'number'
                ? roi.repeat_scan_rate.toFixed(1)
                : null,
            unit: roi
              ? `${roi.repeat_scan_numerator} of ${roi.repeat_scan_denominator} scanned ${
                  roi.repeat_scan_denominator === 1 ? 'item' : 'items'
                }`
              : card.unit,
          }
        default:
          // Registry-backed cards fall through to `applySummary`, which is the ONE
          // place a card's figure-or-empty-state is decided from the summary. The
          // per-id branches above stay because those cards read the per-metric
          // `IRoiMetrics` shape, which the summary does not carry.
          return applySummary(card, summary, onSaved, roi?.reporting_currency)
      }
    })
  }, [roi, summary, onSaved])

  /**
   * The Legal and Logistics cards that read `IRoiMetrics` directly.
   *
   * Split out rather than folded into `applySummary` because the two read DIFFERENT
   * shapes: `applySummary` resolves a registry metric out of the ROI SUMMARY
   * (`contributions[]` / `withheld[]`), whereas these are plain per-metric figures on
   * the response's `data`. A card is handled by exactly one of the two, and anything
   * unmatched here falls through to `applySummary` unchanged — the same arrangement the
   * Marketing switch above already uses.
   */
  const fromMetrics = (card: ROICardDef): ROICardDef | null => {
    if (!roi) return null

    switch (card.id) {
      case 'legal_stolen_reports':
        return { ...card, value: String(roi.stolen_reports) }
      case 'legal_lost_reports':
        return { ...card, value: String(roi.lost_reports) }
      case 'log_items_per_owner':
        return {
          ...card,
          value:
            typeof roi.avg_certificates_per_owner === 'number'
              ? roi.avg_certificates_per_owner.toFixed(2)
              : null,
        }
      case 'log_resales':
        return { ...card, value: String(roi.resales_count) }
      case 'log_avg_resale_price': {
        /*
          ⚠️ THE MIXED-CURRENCY WARNING IS GONE, AND THAT IS THE FIX (VERA-260).

          It used to read: "Resales in this period are priced in N different
          currencies. They are not converted, so this average mixes units..." — a
          sentence correctly describing a figure that was genuinely meaningless ($400
          and ¥400 added and halved). Labelling a wrong number is not a defence of it.
          The reader was handed a clean-looking average, told it could not be used, and
          given nothing they could use instead.

          The average now includes only resales priced in the company's reporting
          currency, so it is a real single-currency figure, and what replaces the
          warning is a plain COUNT of what was left out. That is a fact about the data,
          not a caveat about the arithmetic — the same register `resales_unpriced` and
          `transfer_completion_unstamped` already use on this page. Per the epic's
          tooltip decision, it renders in the ⓘ rather than as a permanent amber
          sentence on the card face.
        */
        const excluded = roi.resales_excluded_currency
        const unpriced = roi.resales_unpriced
        // The average's real denominator. Both exclusions come off it, so the basis
        // line cannot claim rows the figure does not contain.
        const counted = roi.resales_count - unpriced - excluded

        const plural = (n: number, one: string, many: string) =>
          n === 1 ? one : many

        const notes = [
          excluded > 0
            ? `${excluded} completed ${plural(excluded, 'resale is', 'resales are')} priced in a different currency and ${plural(excluded, 'is', 'are')} not included. Prices are never converted, so a figure mixing currencies would not mean anything.`
            : null,
          unpriced > 0
            ? `${unpriced} completed ${plural(unpriced, 'resale carries', 'resales carry')} no declared price and ${plural(unpriced, 'is', 'are')} left out of this average.`
            : null,
          /*
            The ASSUMPTION, stated. `transfers.currency` is nullable and mostly unset,
            so an unlabelled price is taken as being in the reporting currency rather
            than discarded — otherwise the metric collapses to an em-dash over a column
            brands never fill. Saying so is the whole difference between an assumption
            and a silent one, and it is only worth saying when the figure would read
            differently without it, so it is suppressed when nothing is unlabelled.
          */
          roi.resales_unlabelled_currency > 0
            ? `${roi.resales_unlabelled_currency} of the ${plural(roi.resales_unlabelled_currency, 'resales counted has', 'resales counted have')} no currency recorded, and ${plural(roi.resales_unlabelled_currency, 'is', 'are')} treated as ${roi.reporting_currency}.`
            : null,
        ].filter(Boolean)

        return {
          ...card,
          value:
            typeof roi.avg_resale_price_cents === 'number'
              ? formatCents(roi.avg_resale_price_cents, roi.reporting_currency)
              : null,
          // Major units, ungrouped — the second of the two `formatCents` sites, and the
          // only money branch outside `applySummary`. See `ROICardDef.rawValue`.
          rawValue:
            typeof roi.avg_resale_price_cents === 'number'
              ? String(roi.avg_resale_price_cents / 100)
              : null,
          unit:
            roi.resales_count > 0
              ? `over ${counted} of ${roi.resales_count} ${plural(roi.resales_count, 'resale', 'resales')}`
              : card.unit,
          note: notes.length > 0 ? notes.join(' ') : undefined,
        }
      }
      case 'log_resales_per_item':
        return {
          ...card,
          value:
            typeof roi.avg_resales_per_resold_certificate === 'number'
              ? roi.avg_resales_per_resold_certificate.toFixed(2)
              : null,
        }
      case 'log_avg_transfer_time':
        /*
          VERA-260 — REAL as of `transfers.completed_at` (migration 0095). It was a
          permanent em-dash before that, because the only other timestamp was
          `updated_at`, which any write bumps.

          The `note` is the ramp, and it is the important half. Historical transfers
          were deliberately NOT backfilled (backfilling from `updated_at` would have
          invented a duration for every one of them, with nothing marking which
          figures were measured), so for a while this median is computed over recent
          transfers only. Saying how many are missing is what stops a brand reading a
          thin sample as their whole history — and unlike the caveats elsewhere in
          this file, this one disappears on its own as transfers complete.
        */
        return {
          ...card,
          value:
            typeof roi.median_days_transfer_completion === 'number'
              ? roi.median_days_transfer_completion.toFixed(1)
              : null,
          unit:
            roi.transfer_completion_measured > 0
              ? `median days over ${roi.transfer_completion_measured} ${
                  roi.transfer_completion_measured === 1 ? 'transfer' : 'transfers'
                }`
              : card.unit,
          note:
            roi.transfer_completion_unstamped > 0
              ? `${roi.transfer_completion_unstamped} completed ${
                  roi.transfer_completion_unstamped === 1 ? 'transfer' : 'transfers'
                } here finished before Vera began recording completion times, so ${
                  roi.transfer_completion_unstamped === 1 ? 'it is' : 'they are'
                } not counted. This gap closes on its own as new transfers complete.`
              : undefined,
        }
      case 'log_time_to_first_resale':
        return {
          ...card,
          value:
            typeof roi.median_days_to_first_resale === 'number'
              ? roi.median_days_to_first_resale.toFixed(1)
              : null,
          unit:
            roi.first_resale_measured > 0
              ? `median days over ${roi.first_resale_measured} resold ${
                  roi.first_resale_measured === 1 ? 'item' : 'items'
                }`
              : card.unit,
        }
      case 'log_product_lifespan': {
        /*
          ⚠️ THE BASIS SPLIT IS IN `unit`, NOT `note`, AND THAT PLACEMENT IS THE WHOLE
          POINT (VRG-517).

          The median is computed over TWO different origins — `manufactured_at` where a
          product has one, earliest certificate creation where it does not. Those are
          different measurements ("how old is this thing" vs "how long has it been in
          the system"), so a bare number blends two quantities and means neither. The
          server returns the split precisely so the client can state it.

          `note` cannot carry it. `AnalyticsBlock` routes `note` into the `(i)` tooltip
          whenever the tile HAS a figure (`showCaveatInline = Boolean(note) &&
          statIsEmpty`) — so exactly when the number is on screen, a `note` is behind a
          hover. A disclosure that is only reachable by hovering is not beside the
          number, and the reader who most needs it is the one who never hovers.

          `unit` is the basis line, rendered directly under the figure on the face. That
          is where "what was counted" already lives on every other tile, and the origin
          IS part of what was counted here.

          `note` USED TO KEEP the two things that read as caveats-on-a-correct-figure —
          right-censoring, and the import-date bias the fallback introduces — and now keeps
          neither. Censoring moved into the card's `description`, which IS the tooltip body,
          so holding it here too printed it twice in one bubble; the import-date bias was the
          same split `basis` already prints on the face, in more words. See the `note` comment
          on the return below for the consequence to the `(i)`'s caveat disc.
        */
        const measured = roi.product_lifespan_measured
        const fromManufacture = roi.product_lifespan_measured_from_manufacture
        // Derived, not returned: a third server field could disagree with these two.
        const fromCreation = Math.max(measured - fromManufacture, 0)
        const products = measured === 1 ? 'product' : 'products'

        /*
          Three shapes, because naming a split that does not exist is noise and hiding
          one that does is the failure above. A single-basis tile still NAMES its basis
          — the reader cannot otherwise tell which of the two they are looking at, and
          the all-fallback case is the common one today (nothing populates
          `manufactured_at`), so it is the case that most needs saying.

          "median over N", NOT "median days over N" — the word moved UP onto the figure
          (see `value` below). Leaving it in both places printed "362.1 days" above "median
          days over 8 products", which reads as a stutter rather than as a basis.

          The `measured === 0` branch keeps `card.unit`'s "median days observed" untouched, and
          that is deliberate rather than an oversight: with no figure the face shows an em-dash,
          so there is no "362.1 days" above it for the word to have moved onto, and the basis
          line is the only place left saying what the metric would have been counted in.
        */
        const basis =
          measured === 0
            ? card.unit
            : fromManufacture === 0
              ? `median over ${measured} ${products}, measured from certificate creation`
              : fromManufacture === measured
                ? `median over ${measured} ${products}, measured from manufacture date`
                : `median over ${measured} ${products} · ${fromManufacture} from manufacture date, ${fromCreation} from certificate creation`

        return {
          ...card,
          /*
            THE UNIT RIDES ON THE FIGURE, exactly as the rate cards' `%` does
            (`mkt_invalid_scan_rate`, `mkt_repeat_scan_rate`) — that is the existing mechanism
            here, and there is no `suffix` prop on `AnalyticsBlock` to reach for instead.

            A bare "362.1" is the one reading of this tile that is actively wrong rather than
            merely terse: the neighbouring figures are counts and percentages, so an unqualified
            three-digit number in the same row reads as a count of something. Days is the only
            thing it is not obviously a count of.
          */
          value:
            typeof roi.median_product_lifespan_days === 'number'
              ? `${roi.median_product_lifespan_days.toFixed(1)} days`
              : null,
          // Without the word, so the export's column is a number rather than text — the same
          // split the rate cards make between `value` and `rawValue`.
          rawValue:
            typeof roi.median_product_lifespan_days === 'number'
              ? roi.median_product_lifespan_days.toFixed(1)
              : null,
          unit: basis,
          /*
            NO `note`, and dropping it is the point rather than a side-effect (VRG-517).

            It carried two sentences. The first — "this measures how long items are still being
            scanned for, so it grows while they stay in use" — is now the SECOND sentence of the
            card's `description`, i.e. the tooltip body itself, so keeping it here would print
            the same fact twice in one bubble. The second restated the per-basis split that
            `basis` above already prints on the card face, in more words.

            Consequence worth stating: with no caveat, the `(i)` loses its `caveatMarkSx` disc
            and renders as a plain glyph. That is correct here — the disc means "there is
            something attached to this number beyond the definition", and there no longer is.
            A figure that is absent still gets no note, exactly as before: this branch only ran
            when `median_product_lifespan_days` was a number.
          */
        }
      }
      default:
        return null
    }
  }

  const cards = useMemo<Record<ROITabKey, ROICardDef[]>>(
    () => ({
      marketing,
      legal: ALL_ROI_CARDS.legal.map(
        card =>
          fromMetrics(card) ??
          applySummary(card, summary, onSaved, roi?.reporting_currency),
      ),
      logistics: ALL_ROI_CARDS.logistics.map(
        card =>
          fromMetrics(card) ??
          applySummary(card, summary, onSaved, roi?.reporting_currency),
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [marketing, roi, summary, onSaved],
  )

  /** Flat lookup by card id — the shape the canvas's tile list wants. */
  const byId = useMemo(
    () => new Map(Object.values(cards).flat().map(c => [c.id, c])),
    [cards],
  )

  return { cards, byId, isLoading }
}
